import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from 'react-query';
import { UserService } from '../services/UserService';
import Loader from '../components/Loader';
import { PAGINATION_DEFAULT } from '../constants';
import InfiniteScroll from 'react-infinite-scroll-component';
import Post from '../components/Post';
import { useAuthStore } from '../store/zustand';
import UserHeader from '../components/UserHeader';
import UserSidebar from '../components/UserSidebar';
import { TimelineService } from '../services/TimelineService';
import PostDetail from '../components/PostDetail';
import { usePost } from '../hooks/usePost';
import NotFoundError from '../components/NotFoundError';
import GeneralError from '../components/GeneralError';
import CreatePostModal from '../components/CreatePostModal';
import { IonIcon } from '@ionic/react';
import { addCircleOutline } from 'ionicons/icons';
import Layout from '../components/Layout';

const UserPosts = () => {
  const [searchParams] = useSearchParams();

  const currentUser = useAuthStore((state) => state.user);
  const { selectPost } = usePost();

  const { userId } = useParams();

  const [error, setError] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [isFriend, setIsFriend] = useState(false);

  const getUserQuery = useQuery({
    queryKey: ['userProfile', userId],
    queryFn: async () => {
      return await UserService.getUser(userId as string);
    },
    onError: async (e: any) => {
      if (e?.response?.status === 404 || e?.response?.status === 400) {
        setNotFound(true);
      } else {
        setError(true);
      }
    },
  });

  const getUserTimelineQuery = useInfiniteQuery(
    ['userTimeline', userId],
    // @ts-ignore
    TimelineService.getUserTimeline,
    {
      getNextPageParam: (result) =>
        result?.posts?.length === PAGINATION_DEFAULT
          ? result.offset + PAGINATION_DEFAULT
          : undefined,
      onError: async (e: any) => {
        if (e?.response?.status === 404 || e?.response?.status === 400) {
          setNotFound(true);
        } else {
          setError(true);
        }
      },
    },
  );

  useEffect(() => {
    if (searchParams.has('post')) {
      selectPost(searchParams.get('post') as string);
    }
  }, [searchParams]);

  useEffect(() => {
    if (getUserQuery?.data && currentUser) {
      setIsFriend(
        getUserQuery.data?.isFriend !== undefined
          ? getUserQuery.data?.isFriend
          : false,
      );
    }
  }, [currentUser, getUserQuery]);

  return (
    <>
      {error || notFound ? (
        <>{error ? <GeneralError /> : <NotFoundError />}</>
      ) : (
        <Layout>
          <div className="max-w-[1065px] mx-auto pb-5 sm:pb-10">
            {(getUserQuery.isLoading || getUserTimelineQuery.isLoading) && (
              <Loader />
            )}

            {getUserQuery.data && (
              <>
                <UserHeader
                  profileUser={getUserQuery.data}
                  activeTab={'posts'}
                  currentUser={currentUser}
                  isFriend={isFriend}
                />

                <div
                  className="flex 2xl:gap-12 gap-6 mt-6 max-lg:flex-col"
                  id="js-oversized"
                >
                  <div className="flex-1 lg:order-1 order-2">
                    {getUserTimelineQuery.data && (
                      <InfiniteScroll
                        dataLength={
                          getUserTimelineQuery?.data
                            ? getUserTimelineQuery?.data?.pages?.flatMap(
                                (page) => page.posts,
                              )?.length || 0
                            : 0
                        }
                        next={getUserTimelineQuery.fetchNextPage}
                        hasMore={getUserTimelineQuery.hasNextPage || false}
                        loader={<div className="my-10">Loading more...</div>}
                      >
                        <div className="space-y-6">
                          {getUserTimelineQuery?.data?.pages.map((group, i) => (
                            <React.Fragment key={i}>
                              {group.posts.map((post, idx) => (
                                <Post
                                  key={idx}
                                  currentUser={currentUser}
                                  post={post}
                                />
                              ))}
                            </React.Fragment>
                          ))}
                        </div>
                      </InfiniteScroll>
                    )}
                  </div>

                  <div className="lg:w-[400px] lg:order-2 order-1">
                    <UserSidebar profileUser={getUserQuery.data} />
                  </div>
                </div>

                <PostDetail currentUser={currentUser} />

                {currentUser?.id === userId && (
                  <>
                    <CreatePostModal targetId={`create-post`} />

                    <div>
                      <button
                        type="button"
                        className="sm:m-10 m-5 px-4 py-2.5 rounded-2xl bg-gradient-to-tr from-[#e232ff] to-[#db00ff] text-white shadow fixed bottom-0 right-0 group flex items-center gap-1"
                        data-uk-toggle={`target: #create-post`}
                      >
                        <IonIcon icon={addCircleOutline} className="text-2xl" />

                        <div className="text-base font-semibold">Post</div>
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </Layout>
      )}
    </>
  );
};

export default UserPosts;
